/**
 * Ops inbox (path /ops): passcode prompt (kept in memory only, never
 * storage), then the requests table with per-row status-transition
 * actions. Every call carries the passcode as the x-ops-passcode header;
 * a 401 on any call re-prompts (passcode never persisted, so a page
 * reload always re-prompts too).
 *
 * Prices (P6, 2026-07-21): the Price action records an actual euro amount
 * and the table shows it; Update price corrects it until Confirmed. This
 * surface is OPERATOR-side — the nurse list endpoint and screens stay
 * amount-free (the "customers never see a price" rule).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { ApiClient } from '../api.js';
import type {
  AskKind,
  AskState,
  DeclineReason,
  PayerKind,
  RequestStatus,
  SchedulePattern,
  TimeMeaning,
} from '../../shared/request-schema.js';
import { dublinDayNumber, formatWhenTwoLines, parseEuroToCents, formatEuroFromCents } from '../format.js';
import { opsCopy, myRequestsCopy, requestFormCopy, sharedCopy } from '../copy.js';
import { AccessReview } from './AccessReview.js';
import { Button, Card, Chip, FormField, formatWhenParts, toneFor } from '@ecmb/ui';

type OpsRequestItem = {
  requestId: number;
  /**
   * ⭐ T1683 M3 (A1), AND THIS TYPE IS WIDENED **DELIBERATELY**, not
   * incidentally. `quote-helper/src/core/portal-ops-projection.ts`'s
   * MINIMISATION-DEPTH ruling is a PARITY CLAIM against this local type - "the
   * kiosk keeps exactly what the portal's own operator screen DISPLAYS" - so a
   * DISPLAYED field added to that keep-set and not to this one would leave the
   * ruling stating something false. M3 chose to keep the claim TRUE rather than
   * restate it as prose, because the alternative - the seam carrying a field the
   * portal's own operator cannot see - is the wrong way round for a reference
   * whose whole purpose is that BOTH offices can read it out. (At M3 that
   * rejected alternative was kiosk 13 / this screen 12.)
   *
   * ⛔⛔ SINCE T1683 M6 THE TWO COUNTS NO LONGER MATCH, AND THAT IS CORRECT
   * RATHER THAN A REGRESSION. ⛔ RE-DERIVED AT M7, WHICH THE M6 WORDING ASKED
   * FOR BY NAME (it said "M7 widens the seam again; re-derive both numerals
   * there"): at M7 the kiosk kept FIFTEEN and this screen showed THIRTEEN.
   * ⭐⭐ RE-DERIVED AGAIN AT T2088 M7 (rework), AND TWO OF THE THREE FIGURES THE
   * T1730 SENTENCE STATED HAD ROTTED — it said the kiosk keeps SEVENTEEN and that
   * this screen adds THREE converse fields (`room` plus a next-of-kin pair), and
   * the pair is not on this type AT ALL: T1868 M6 deleted it. The kiosk file was
   * corrected in this milestone and this mirror was not, so the two contradicted
   * each other for the length of one commit. ⛔ NEVER TRANSCRIBE THESE FROM THE
   * KIOSK FILE — count this type's own fields. Run from `portal/`; the figure each
   * printed on 2026-09-18 (board 2188: pickupAt joined) is written beside it, so a command that has gone SILENT
   * (a `sed` range whose anchor no longer matches prints `0`, with no error — the
   * failure this rework found in the kiosk file's own UPSTREAM form) is tellable
   * from one that still measures:
   *   SCREEN   = sed -n '/^type OpsRequestItem = {/,/^};/p' src/client/screens/Ops.tsx \
   *                | grep -cE '^  [a-zA-Z]+\??:'                                -> 16
   *   KIOSK    = sed -n '/^export const PORTAL_REQUEST_KEEP_KEYS/,/^] as const;/p' \
   *                ../quote-helper/src/core/portal-ops-projection.ts | grep -c "^  '"  -> 20
   *              (KIOSK, SCREEN and the three set forms below re-run from
   *              `portal/` on 2026-10-01, board 2707 M1: only KIOSK moved, 19 -> 20)
   *   and the two SETS, which is what a parity claim is actually about (zsh):
   *     s() { sed -n '/^type OpsRequestItem = {/,/^};/p' src/client/screens/Ops.tsx \
   *             | grep -oE '^  [a-zA-Z]+' | tr -d ' ' | sort; }
   *     k() { sed -n "/^export const PORTAL_REQUEST_KEEP_KEYS/,/^] as const;/p" \
   *             ../quote-helper/src/core/portal-ops-projection.ts \
   *             | grep -oE "^  '[a-zA-Z]+'" | tr -d " '" | sort; }
   *     comm -12 <(s) <(k) | wc -l     # SHARED   -> 15
   *     comm -23 <(s) <(k)             # CONVERSE -> room   (ONE field, not three)
   *     comm -13 <(s) <(k)             # kiosk-only -> kioskDestinationId,
   *                                    #   returnTrip, returnAt, returnAtState,
   *                                    #   createdAt (board 2707 M1)
   * ⇒ the kiosk keeps TWENTY, this screen shows FIFTEEN of them, and the one
   * converse field that crosses no seam is `room`.
   * ⚠️ THIS PARAGRAPH IS THE WHOLE RECONCILIATION - without it the numerals
   * above read as a rule M6 broke, and the obvious "fix" is to restore parity by
   * adding the missing keys here. DO NOT, and the two have DIFFERENT reasons:
   *   · `kioskDestinationId` (M6) is a REACHABILITY KEY - an opaque kiosk
   *     catalogue id nobody reads aloud and no operator screen on either side
   *     displays - so a column here would be meaningless. The kiosk file draws
   *     the same distinction for `nurseId` in
   *     `PORTAL_ACCESS_REVIEW_NURSE_KEEP_KEYS`.
   *   · `returnTrip` (M7) IS a displayed fact, so it needs a different reason,
   *     and the reason is the CONSUMER. The doubling happens on the KIOSK's
   *     screen, which is the only surface that prices anything; this screen
   *     prices nothing, and the nurse's own form and list already show her
   *     answer back to her. A third rendering on a screen that cannot act on it
   *     is clutter, not parity.
   * ⇒ the parity claim is about DISPLAYED FIELDS, and it is now stated as a rule
   * rather than as a count: nothing crosses that the portal operator screen does
   * not display, EXCEPT keys named with their own justification.
   *
   * ⭐⭐ T1730 (Alex, 2026-09-03, q731; ADR-0002 §12 / ADR2-D15) -
   * `accessibleVehicle` IS DISPLAYED ON BOTH SIDES, so it is a PARITY field and
   * not a third exception. The consumer argument for `returnTrip` was that a
   * fact belongs on the screen that can act on it; Helen dispatches the
   * wheelchair-accessible bus from the request wherever she reads it, and Alex
   * ruled it shown here AND on the kiosk row (board 1730). ⛔ ONE BIT, NEVER
   * THE LIST: it is derived in `listAllRequests` from the nurse's `wheelchair`
   * tick; the raw `equipment` array stays off this type and off the kiosk
   * keep-set. ⚠️ PRECISELY: `/api/ops/requests` still returns the repo row
   * UNPROJECTED (as it did before T1730), so the array does reach THIS
   * browser in the payload and is narrowed at render by this local type; the
   * kiosk narrows it server-side before serving. A server-side PICK for the
   * ops wire is the follow-up beside the full-list column below (T1730
   * security review, advisory 1). The full equipment list on this table is a
   * follow-up for Alex (board 1730), not a thing to "restore" here. Vocabulary: this screen says
   * "Wheelchair" (the nurse's own word, `EQUIPMENT_OPTIONS[0]`); the kiosk says
   * "Accessible vehicle" (its registry word), so the fact reads the same on
   * Helen's kiosk row before the crossing and on her Inbox after it.
   *
   * ⛔ null on every request written before M3; the cell renders empty rather
   * than a placeholder.
   */
  jobRef: string | null;
  homeName: string;
  nurseDisplayName: string;
  residentDisplayName: string;
  /**
   * ⭐⭐ T1717 M4 (DD9) — ROOM, AND THIS SCREEN IS THE ONLY OPERATOR SURFACE
   * THAT SHOWS IT.
   *
   * ⚠️ THIS BLOCK SAID "ROOM AND THE NEXT-OF-KIN PAIR … these THREE are the
   * converse" UNTIL T2088 M7 (rework). T1868 M6 DELETED the pair from this type;
   * the converse set is now exactly ONE field, re-derived with the `comm -23`
   * command in the `jobRef` docblock above (-> `room`). The DD9 ruling below is
   * kept in full because it is what stops the pair being re-added, not because
   * the pair is still here.
   *
   * ⛔⛔ THIS IS THE ONE PLACE THE PARITY CLAIM ABOVE RUNS THE OTHER WAY, AND
   * THAT IS THE RULING RATHER THAN A DRIFT. The claim is "nothing crosses the
   * seam that this screen does not display"; `room` is the converse —
   * displayed HERE and deliberately NOT in `PORTAL_REQUEST_KEEP_KEYS`. Captain
   * ruled it binding (plan DD9): next of kin stays off every kiosk screen, the
   * kiosk database and every kiosk-served response, and the PRD's "arrive on
   * Helen's kiosk" is met on this table instead. ⛔ DO NOT "restore parity" by
   * adding `room` to the kiosk keep-set, and do not re-add next of kin here —
   * either is a widening needing three mirrors, a census re-scope and a fresh
   * Article 9 purpose argument, and the recommended default is explicitly no
   * widening.
   * ⚠️ `room` was ALREADY dropped by that keep-set as Article 9-adjacent before
   * T1717 M4; it is here for the same reason the pair once was — Helen
   * dispatches a driver to a door, and the room is which door.
   */
  room: string;
  /** ⭐ T1730 - the one bit from the nurse's equipment tick, beside Room because
   *  it is also a fact about the person the driver is collecting. Derived in
   *  `core/requests-repo.ts` (`listAllRequests`); never the list. */
  accessibleVehicle: boolean;
  destinationName: string;
  payerKind: PayerKind;
  journeyDate: string | null;
  /**
   * ⭐⭐ board 2188 (Alex, 2026-09-18) — THE DERIVED COLLECTION, AND IT MOVES
   * THIS KEY OUT OF THE CENSUS'S EXCEPTION LIMB ABOVE, which is the one edit the
   * docblock's own rule permits. That rule is *"nothing crosses that the portal
   * operator screen does not display, EXCEPT keys named with their own
   * justification"* — it constrains what may CROSS, not what may be DISPLAYED, so
   * a key that starts being displayed simply stops needing an exception.
   * ⚠️ The PRECEDENT is `accessibleVehicle` (T1730), not the `returnTrip`
   * exception: `returnTrip`'s stated ground is PRICING (the doubling happens on
   * the kiosk, the only surface that prices), whereas T1730 records that a field
   * Alex rules displayed on BOTH sides becomes a parity field rather than a third
   * exception. The kiosk already displays it, and Helen dispatches from this
   * screen, so the collection is a fact she acts on.
   * ⛔ The kiosk-only list above therefore drops from FIVE to FOUR; its mirror in
   * `quote-helper/src/core/portal-ops-projection.ts` was corrected in the SAME
   * change, because nothing compiles across the two trees and the repo has already
   * recorded one commit where the two contradicted each other.
   */
  pickupAt: string | null;
  schedulePattern: SchedulePattern | null;
  /** T632. Helen's ONLY read surface is this table, so a marker missing from
   *  this local type is a marker she can never act on. */
  timeMeaning: TimeMeaning | null;
  status: RequestStatus;
  waitingNotes: string;
  pricedAmountCents: number | null;
  /**
   * ⭐ Board 2788 M4 (DD-16, S21) - THE HOME'S CHANGE AND ITS ASK, SHOWN HERE
   * READ-ONLY, so the kiosk's keep-set never carries a displayed fact this screen
   * does not show (the parity rule above). ⛔ NO DECISION CONTROL: accept,
   * decline and reschedule are the kiosk's, because they pass its money checks
   * (DD-4) and this screen has none. `revision` and `askSeq` also cross to the
   * kiosk and are REACHABILITY keys (every kiosk act posts them back), so like
   * `kioskDestinationId` they are not displayed. Re-derive the sets with the
   * `comm` forms in the `jobRef` docblock above.
   */
  homeChanged: boolean;
  askKind: AskKind | null;
  askState: AskState;
  askJourneyDate: string | null;
  askPickupAt: string | null;
  askReturnAt: string | null;
  askDeclineReason: DeclineReason | null;
  /** The ops-shape history (`listRequestHistoryForOps`): which side acted, never
   *  which person. Newest first, capped by the server. */
  history: OpsHistoryLine[];
};

type OpsHistoryLine = { kind: string; fromAt: string | null; toAt: string | null; at: string; by: 'home' | 'ops' };

/**
 * ⭐ Board 2788 M4 (DD-16) - the words for the read-only change and ask lines.
 * Inline, like this file's scroll-region name, because `copy.ts` carries a
 * line-keyed allow-list entry and is M5's file. Flagged for Vicky's sitting.
 * Times are labelled by what the request was made with (DD-11); a history line
 * shows only the request's own stored time, never a derived collection.
 */
const DECLINE_WORDS: Record<DeclineReason, string> = {
  no_bus: 'no bus free at that time',
  too_close: 'too close to the trip to change it here',
  please_ring: 'the home was asked to ring',
};
const HISTORY_WORDS: Record<string, string> = {
  'time-changed': 'Time changed',
  'change-asked': 'Change asked',
  'cancel-asked': 'Cancel asked',
  'ask-declined': 'Ask declined',
  cancelled: 'Cancelled',
  declined: 'Declined',
};

/** A day and a clock in Dublin, through the shared formatter. */
function instantWords(iso: string | null): string {
  const p = formatWhenParts({ journeyDate: iso });
  return p.clock === null ? p.date : `${p.date}, ${p.clock}`;
}

function meaningWord(item: { timeMeaning: TimeMeaning | null }): string {
  return item.timeMeaning === 'appointment' ? 'Appointment' : 'Collection';
}

/** The open ask in one sentence, or null when there is none to show. */
function askWords(item: OpsRequestItem): string | null {
  if (item.askState !== 'open') return null;
  if (item.askKind === 'cancel') return 'The home asked to cancel.';
  if (item.askKind !== 'change' || item.askJourneyDate === null) return null;
  const pickupClock =
    item.timeMeaning === 'appointment' && item.askPickupAt !== null
      ? formatWhenParts({ journeyDate: item.askPickupAt }).clock
      : null;
  const pickup = pickupClock === null ? '' : ` (collection ${pickupClock})`;
  const returnClock = item.askReturnAt === null ? null : formatWhenParts({ journeyDate: item.askReturnAt }).clock;
  const back = returnClock === null ? '' : `, return ${returnClock}`;
  return `The home asked to move it: ${meaningWord(item)} ${instantWords(item.askJourneyDate)}${pickup}${back}.`;
}

function historyWords(item: OpsRequestItem, h: OpsHistoryLine): string {
  const who = h.by === 'ops' ? 'East Coast Minibus' : 'the home';
  const times =
    h.fromAt !== null && h.toAt !== null
      ? `: ${meaningWord(item)} ${instantWords(h.fromAt)} to ${instantWords(h.toAt)}`
      : '';
  return `${HISTORY_WORDS[h.kind] ?? 'Changed'} by ${who}${times} (${instantWords(h.at)})`;
}

export type OpsProps = { api: ApiClient };

/** Legal forward transitions mirrored from requests-repo.ts's
 *  LEGAL_TRANSITIONS, for which action buttons to offer. Purely a UI
 *  convenience: the server is the actual authority, and a stale client
 *  (two ops tabs open) just gets a 409 illegal_transition handled below. */
/** Presentation only: the actions that end a request read as the quiet
 *  choice, so Price is not competing with Decline/Cancel at equal weight.
 *  Derived from the status already in hand, not new state. */
const DESTRUCTIVE = new Set<string>(['declined', 'cancelled']);

const ACTIONS_FOR_STATUS: Record<string, Array<{ status: RequestStatus; label: string }>> = {
  new: [
    { status: 'priced', label: opsCopy.actionPrice },
    { status: 'declined', label: opsCopy.actionDecline },
    { status: 'cancelled', label: opsCopy.actionCancel },
  ],
  priced: [
    // priced -> priced is the re-price path (P6); the server allows it
    // until Confirmed.
    { status: 'priced', label: opsCopy.actionReprice },
    // ⛔ Board 2814 M1 (plan DD-3, S1) - NO CONFIRM HERE ANY MORE. Confirming is
    // the kiosk's one press, which makes the booking first and marks the request
    // second; a Confirm on this screen confirmed the home's request with no booking
    // behind it. The portal route is unchanged, so a passcode holder can still do
    // that through the API: ADR-0003 records the residual and its owner.
    { status: 'declined', label: opsCopy.actionDecline },
    { status: 'cancelled', label: opsCopy.actionCancel },
  ],
  // A confirmed run — a standing regular trip especially — can still be
  // stopped from the operator side (P5).
  confirmed: [{ status: 'cancelled', label: opsCopy.actionCancel }],
};

/** Statuses whose rows show the euro input (anything that can still move
 *  to 'priced'). */
const PRICEABLE = new Set<string>(['new', 'priced']);

/**
 * The `when` cell, as the two lines the shared formatter returns.
 *
 * ⛔ TWO NODES, NOT A JOINED STRING, so the day and the time are two facts on two
 * lines in a narrow cell.
 * ⛔⛔ AND THE `<br>` ALONE DID NOT DO THE JOB THE FIRST COMMENT CLAIMED (T1749 M4
 * fold, impeccable). It said joining would let the browser break "between the
 * meaning word and its clock" — and the `<br>` form still did: measured live at
 * 390px, the cell rendered `Tue 22 Sept` / `Appointment` / `01:25`, three lines,
 * with the meaning word orphaned from the time it qualifies. A line break is a
 * break OPPORTUNITY, not a fence. The time line therefore carries a class and the
 * portal sheet holds it on one line with `white-space: nowrap`; the day is left
 * free to wrap, because a day that wraps still reads as one fact.
 */
function whenCell(item: {
  journeyDate: string | null;
  /** ⛔ board 2188 — NAMED, NEVER WIDENED TO THE ROW. `format.ts`'s own note says
   *  why: `WhenInput` also accepts `timeZone` and `locale`, so handing the wire
   *  object through would let a row override the pinned Europe/Dublin pair. */
  pickupAt: string | null;
  schedulePattern: SchedulePattern | null;
  timeMeaning: TimeMeaning | null;
}) {
  const when = formatWhenTwoLines(item);
  // ⭐⭐ THE DERIVED COLLECTION, ON HELEN'S OWN TABLE (Alex, 2026-09-18, board 2188).
  // ⛔ THIS SCREEN HAS NO `when.pickup` SLOT, and that is why it needed a ruling the
  // two cards did not. They build a `CardRequestWhen`, whose `pickup` slot is free on
  // an `appointment` row; `formatWhenTwoLines` returns `{date, timeLine}` and has
  // nowhere to put a second clock. So the word here is a CHOICE, and Alex made it:
  // `Collection`, the register's existing word (`format-when.ts` `MEANING_WORDS`),
  // read beside the `Appointment` line the same cell already prints.
  // ⚠️ NO NEW CLASS. `.ops-when-time` already exists and already carries the
  // `white-space: nowrap` this line needs for the same reason the first one does —
  // measured at T1749 M4, a `<br>` is a break OPPORTUNITY, not a fence, and an
  // unfenced line orphans the meaning word from its clock at 390px. Reusing it is
  // both the minimal treatment and the one the style contract already knows.
  // ⚠️ `appointment` rows only, null-guarded: a `collection` row's own time is
  // already this cell's `timeLine`, and such a row never carries a `pickupAt`.
  // ⛔⛔ THE DAY RIDES WITH IT WHEN IT DIFFERS (security review M1, board 2188).
  // This cell prints ONE date line, from the appointment, so a bare clock here
  // borrows the wrong day. Reachable on LEGITIMATE data: the database allows an
  // offset up to 480 minutes, so an early appointment derives a pickup on the
  // previous calendar day, and Helen would dispatch against the wrong date.
  const collectionParts =
    item.timeMeaning === 'appointment' && item.pickupAt !== null
      ? formatWhenParts({ journeyDate: item.pickupAt })
      : null;
  const collection =
    collectionParts === null || collectionParts.clock === null
      ? null
      : dublinDayNumber(item.pickupAt) === dublinDayNumber(item.journeyDate)
        ? collectionParts.clock
        : `${collectionParts.clock} (${collectionParts.date})`;
  return (
    <>
      {when.date}
      <br />
      <span class="ops-when-time">{when.timeLine}</span>
      {collection !== null && (
        <>
          <br />
          <span class="ops-when-time">
            {opsCopy.collectionLine} {collection}
          </span>
        </>
      )}
    </>
  );
}

export function Ops({ api }: OpsProps) {
  const [passcode, setPasscode] = useState('');
  // In-memory only per the brief; never written to storage, so a reload
  // always re-prompts.
  const [authedPasscode, setAuthedPasscode] = useState<string | null>(null);
  const [passcodeError, setPasscodeError] = useState<string | null>(null);
  const [items, setItems] = useState<OpsRequestItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [waitingNotes, setWaitingNotes] = useState<Record<number, string>>({});
  // Per-row euro text as typed; parsed to cents only on the Price action.
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [amountErrors, setAmountErrors] = useState<Record<number, string>>({});
  const [showAccessReview, setShowAccessReview] = useState(false);
  const passcodeRef = useRef<HTMLInputElement>(null);

  /**
   * ⛔⛔ T1749 M9 rework 2 — THIS EFFECT IS WHAT KEEPS THE REFUSAL AUDIBLE, and it
   * is a PRECONDITION of the `FormField` adoption below rather than a flourish.
   *
   * The gate used to render `<p class="field-error" role="alert">`, which
   * announces the moment it appears. `FormField`'s `error` slot deliberately
   * carries NO alert role (`FormField.tsx`: a per-field message is not an
   * interruption) and is announced through `aria-describedby` — which is only
   * heard when something LANDS on the control. The three sign-in screens solve
   * that by focusing the field in their `refuse()`; this screen cannot, because
   * `rePrompt` is ALSO called from `act()` mid-session, when the passcode form is
   * not mounted and a ref would be null. An effect fires after the gate mounts,
   * so both doors — a wrong passcode at the gate, and a 401 on a row action —
   * land Helen on the field with the reason attached.
   * ⚠️ IT ALSO REPLACES THE `autoFocus` the bare input carried: `FormField` has no
   * such prop, and it is not owed one — a prop with one call site is the drift
   * that file's own withdrawal record refuses, and this effect covers the first
   * paint as well.
   */
  useEffect(() => {
    if (authedPasscode === null) passcodeRef.current?.focus();
  }, [authedPasscode, passcodeError]);

  function rePrompt(message: string): void {
    setAuthedPasscode(null);
    setItems(null);
    setPasscodeError(message);
  }

  async function loadWith(pc: string): Promise<void> {
    setListError(null);
    const res = await api.get<{ requests: OpsRequestItem[] }>('/api/ops/requests', { 'x-ops-passcode': pc });
    if (res.kind === 'network-error') {
      setListError(sharedCopy.networkError);
      return;
    }
    if (res.status === 401) {
      rePrompt(opsCopy.passcodeWrong);
      return;
    }
    if (res.status === 503) {
      rePrompt(opsCopy.passcodeDisabled);
      return;
    }
    if (res.status !== 200) {
      setListError(sharedCopy.genericError);
      return;
    }
    setAuthedPasscode(pc);
    setItems(res.body.requests);
  }

  function onPasscodeSubmit(e: JSX.TargetedEvent<HTMLFormElement, Event>): void {
    e.preventDefault();
    setPasscodeError(null);
    void loadWith(passcode);
  }

  async function act(id: number, status: RequestStatus): Promise<void> {
    if (!authedPasscode) return;

    let pricedAmountCents: number | undefined;
    if (status === 'priced') {
      const cents = parseEuroToCents(amounts[id] ?? '');
      if (cents === null) {
        setAmountErrors((prev) => ({ ...prev, [id]: opsCopy.priceInvalidError }));
        return;
      }
      pricedAmountCents = cents;
    }
    setAmountErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    setBusyId(id);
    const notes = waitingNotes[id];
    const res = await api.post(
      `/api/ops/requests/${String(id)}/status`,
      {
        status,
        ...(notes !== undefined ? { waitingNotes: notes } : {}),
        ...(pricedAmountCents !== undefined ? { pricedAmountCents } : {}),
      },
      { 'x-ops-passcode': authedPasscode },
    );
    setBusyId(null);

    if (res.kind === 'network-error') {
      setListError(sharedCopy.networkError);
      return;
    }
    if (res.status === 401) {
      rePrompt(opsCopy.passcodeWrong);
      return;
    }
    if (res.status === 409) {
      setListError(opsCopy.illegalTransitionError);
      return;
    }
    if (res.status === 200) {
      await loadWith(authedPasscode);
      return;
    }
    setListError(sharedCopy.genericError);
  }

  if (!authedPasscode) {
    return (
      <div class="screen ops-screen">
        <h1>{opsCopy.title}</h1>
        {/* ⭐⭐ T1749 M9 rework 2 — HELEN'S GATE TAKES THE SIGN-IN GRAMMAR. It was
            the one door into either tool still built from raw parts: a bare
            `<input>` with no class at all (so it took the portal's element-level
            control rule and nothing else), a hand-written label, a hand-written
            error and a hand-written button, on no card. The three portal sign-in
            screens and the kiosk's were re-cut at M9 (DS-27) to `Card
            shape="cover"` + `FormField` + the 56px gold primary at field width;
            this is the fourth door and it now reads as the same system.
            ⛔ NO NEW CLASS, TOKEN OR FAMILY — every string here is one the two
            sheets already declare, which is why the two censuses are unchanged
            either side of this edit (`python3 tools/css-census/families.py
            --per-sheet`, `shapes.py --per-sheet`).
            ⛔ THE LABEL IS BYTE-IDENTICAL (`opsCopy.passcodeLabel`): four specs
            and two e2e files reach this screen by `Ops passcode` alone
            (`e2e/tests/visual/audit-set.spec.ts`,
            `portal/test/client/conformance.spec.tsx`, `ops-screen.spec.tsx`,
            `enter-submit.spec.tsx`), so the words are the contract. */}
        <Card shape="cover">
          <form onSubmit={onPasscodeSubmit}>
            <FormField
              id="ops-passcode"
              label={opsCopy.passcodeLabel}
              type="password"
              value={passcode}
              onInput={setPasscode}
              error={passcodeError ?? undefined}
              elementRef={passcodeRef}
            />
            <Button variant="primary" size="full" type="submit">
              {opsCopy.passcodeSubmit}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (showAccessReview) {
    return <AccessReview api={api} passcode={authedPasscode} onBack={() => setShowAccessReview(false)} />;
  }

  return (
    <div class="screen ops-screen">
      <h1>{opsCopy.title}</h1>
      <div class="action-row">
        <button type="button" class="btn btn--link" onClick={() => setShowAccessReview(true)}>
          {opsCopy.accessReviewOpen}
        </button>
      </div>
      {/* ⭐ T1011 M6 — the scope of this screen's Cancel, stated in the same
          terms as the kiosk's request inbox. Cancelling writes
          `portal.requests.status` in THIS database; whether a bus is driven and
          billed is `bookings.status` in the kiosk's, and nothing here can reach
          it. ⚠️ ABOVE the table, not per row: it is true of every row, and nine
          copies of it in a nine-column table is how a disclosure gets ignored. */}
      <p class="field-hint">{opsCopy.cancelScopeNote}</p>
      {listError && (
        <p class="field-error" role="alert">
          {listError}
        </p>
      )}
      {!items || items.length === 0 ? (
        <p>{opsCopy.tableEmpty}</p>
      ) : (
        /* ⛔ A NAMED, REACHABLE SCROLL REGION (T1749 M4 fold, ux-review (b)).
            Measured at 390px: `scrollWidth` 1285 against `clientWidth` 342, so
            two thirds of this table — including the `When` column — sat outside
            the box, and the box had no `role`, no name and no `tabindex`. A bare
            scrolling div announces nothing and cannot be reached from a keyboard,
            so those columns were unreachable rather than merely off-screen.
            ⚠️ THE THREE ATTRIBUTES, NOT THE SHARED `TableScroll` COMPONENT, and
            that is the first rung rather than a shortcut: `TableScroll` composes
            `data-table-scroll`, whose shared rule holds `overflow-x` only, while
            this wrapper also declares `-webkit-overflow-scrolling: touch` — the
            momentum hint that makes the sideways drag usable on the iPad this
            screen runs on. Adopting the component today would either drop that
            hint or need a second class it has no prop for. The component is the
            M7-to-M9 adoption target; until then the pattern is identical and the
            markup is three attributes on the div that already exists.
            ⚠️ The name is a literal rather than an `opsCopy` key on purpose:
            `portal/src/client/copy.ts` carries a LINE-KEYED entry in
            `check-copy.sh`'s allow-list and is outside this round's files. */
        <div
          class="ops-table-scroll"
          tabindex={0}
          role="region"
          aria-label="Request queue, scrolls sideways"
        >
        <table class="ops-table">
          <thead>
            <tr>
              <th>{opsCopy.columnReference}</th>
              <th>{opsCopy.columnHome}</th>
              <th>{opsCopy.columnNurse}</th>
              <th>{opsCopy.columnResident}</th>
              <th>{opsCopy.columnRoom}</th>
              <th>{opsCopy.columnWheelchair}</th>
              <th>{opsCopy.columnDestination}</th>
              <th>{opsCopy.columnPayer}</th>
              <th>{opsCopy.columnWhen}</th>
              <th>{opsCopy.columnStatus}</th>
              <th>{opsCopy.columnPrice}</th>
              <th>{opsCopy.columnActions}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.requestId}>
                {/* ⛔ EMPTY CELL, NOT A PLACEHOLDER, when there is no reference:
                    a pre-M3 request genuinely has none and never will. */}
                <td class="ops-ref-col">{item.jobRef ?? ''}</td>
                <td>{item.homeName}</td>
                <td>{item.nurseDisplayName}</td>
                <td>{item.residentDisplayName}</td>
                {/* ⛔ EMPTY CELLS, NEVER PLACEHOLDERS, for the same reason the
                    reference cell above is empty: a "-" in a column Helen scans
                    reads as a fact that was checked and found missing, rather
                    than as a column that did not exist yet. `room` is NOT NULL,
                    so its cell is only empty on a hand-built fixture. */}
                <td>{item.room}</td>
                {/* ⭐ T1730 - "Yes" or an EMPTY cell, never a placeholder and
                    never the equipment list; the header names the fact. */}
                <td>{item.accessibleVehicle ? opsCopy.wheelchairYes : ''}</td>
                <td>{item.destinationName}</td>
                <td>{requestFormCopy.payerLabels[item.payerKind] ?? item.payerKind}</td>
                {/* ⭐ T1749 M4 — TWO LINES, IN DUBLIN. This cell rendered
                    `Sat 15 Aug, 10:00 (appointment)` in the BROWSER's zone,
                    which meant Helen's office machine and the nurse's phone
                    could show the same journey an hour apart. It now renders
                    through the shared formatter (`en-IE`, `Europe/Dublin`) that
                    the nurse row has used since T1723 M7: the day, then the time
                    under the word that says which end of the journey it is.
                    ⛔ A ROW WITH NO READABLE TIME SAYS `Time not stated` rather
                    than nothing: a blank cell in a column Helen scans reads as a
                    fact that was checked and found missing, not as one that was
                    never captured. Declared as a behaviour change in the plan and
                    recorded in DESIGN.md. */}
                <td>{whenCell(item)}</td>
                {/* ⭐⭐ T1749 M9 (Step 0a) — THE LAST `.status-badge` IN EITHER TREE.
                    The pill is `Chip`'s status shape, and the tone comes from
                    `toneFor('request', …)` — the shared `Record<RequestStatus, Tone>`
                    the compiler grades — rather than from the status word
                    interpolated into a class name. The old spelling
                    `status-badge--${status}` could not fail: a sixth enum value
                    shipped an UNSTYLED pill silently, because a class the sheet
                    does not declare paints nothing and says nothing. A total map
                    makes that same mistake a `tsc` error on the map literal.
                    ⛔ THIS IS THE ONLY CHANGE TO THIS SCREEN AT M9. No column is
                    added, removed, reordered or merged and no cell is
                    restructured: the density complaint this table carries needs a
                    SHAPE, and the shape is Alex and Vicky's at a sitting
                    (`docs/OPEN-QUESTIONS.md`, Q143), never a worker's. */}
                <td>
                  {/* ⭐ T2187 - `'operator'`, so THIS table is unchanged. The
                      audience is required now (see `toneFor`); Helen reads
                      `operatorStatusLabels` on the line below, which keeps
                      `New` and `Priced` apart, so her tones stay apart too.
                      The nurse-facing cards ask for `'nurse'`, where the two
                      statuses share the word `Sent` and now share its tone. */}
                  <Chip shape="status" tone={toneFor('request', item.status, 'operator')}>
                    {myRequestsCopy.operatorStatusLabels[item.status] ?? item.status}
                  </Chip>
                  {/* ⭐ Board 2788 M4 (DD-16) - READ-ONLY: the change and the ask
                      as words under the status, and the history. Nothing here
                      acts; Helen answers on the kiosk. Every line is guarded,
                      so a row with no change renders exactly as before. */}
                  {item.homeChanged === true && (item.status === 'new' || item.status === 'priced') && (
                    <p class="field-hint">Changed by the home.</p>
                  )}
                  {askWords(item) !== null && <p class="field-hint">{askWords(item)}</p>}
                  {item.askState === 'declined' && item.askDeclineReason !== null && (
                    <p class="field-hint">Ask declined: {DECLINE_WORDS[item.askDeclineReason]}.</p>
                  )}
                  {(item.history ?? []).map((h, i) => (
                    <p class="field-hint" key={`${h.at}-${String(i)}`}>
                      {historyWords(item, h)}
                    </p>
                  ))}
                </td>
                <td class="ops-price-cell">
                  {item.pricedAmountCents !== null ? formatEuroFromCents(item.pricedAmountCents) : ''}
                </td>
                <td class="ops-actions-cell">
                  {PRICEABLE.has(item.status) && (
                    <>
                      <label class="field-label" for={`ops-amount-${String(item.requestId)}`}>
                        {opsCopy.priceInputLabel}
                      </label>
                      <input
                        id={`ops-amount-${String(item.requestId)}`}
                        type="text"
                        inputMode="decimal"
                        placeholder={opsCopy.priceInputPlaceholder}
                        value={
                          amounts[item.requestId] ??
                          (item.pricedAmountCents !== null ? (item.pricedAmountCents / 100).toFixed(2) : '')
                        }
                        onInput={(e) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [item.requestId]: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                      {amountErrors[item.requestId] && (
                        <p class="field-error" role="alert">
                          {amountErrors[item.requestId]}
                        </p>
                      )}
                    </>
                  )}
                  <label class="field-label" for={`ops-notes-${String(item.requestId)}`}>
                    {opsCopy.waitingNotesLabel}
                  </label>
                  <input
                    id={`ops-notes-${String(item.requestId)}`}
                    type="text"
                    maxLength={500}
                    value={waitingNotes[item.requestId] ?? item.waitingNotes}
                    onInput={(e) =>
                      setWaitingNotes((prev) => ({
                        ...prev,
                        [item.requestId]: (e.target as HTMLInputElement).value,
                      }))
                    }
                  />
                  {(ACTIONS_FOR_STATUS[item.status] ?? []).map((action) => (
                    <button
                      key={action.status}
                      type="button"
                      /* ⭐⭐ T1749 M7 (Q140, Alex 2026-09-11, ask 892) — A SWAP NOW,
                         AND THE SIZING RULE MOVED TO MEET IT. This was `btn--secondary`
                         plus a CONDITIONAL outline class on the destructive actions:
                         the one site in the portal where the retired class was not a
                         plain literal, and the reason it re-points to
                         `destructive-quiet` rather than to `secondary` like the other
                         four. A control that wears a different look only when it
                         DESTROYS is not a fifth level, it is the level DESIGN.md §5.1
                         already rules.
                         ⛔ THE OLD COMMENT'S WARNING IS HONOURED, NOT IGNORED: it said
                         a swap would fall back to base `.btn` at 56px and give back
                         the row height the two-up layout won. So `.ops-table`'s
                         sizing rule now names `.btn--quiet` beside `.btn--secondary`
                         (app.css), and both branches keep the 44px caption box. */
                      class={`btn ${DESTRUCTIVE.has(action.status) ? 'btn--quiet btn--destructive' : 'btn--secondary'}`}
                      disabled={busyId === item.requestId}
                      onClick={() => void act(item.requestId, action.status)}
                    >
                      {action.label}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
